import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { getEnvVar } from '../config/env';
import type { ApiResponse, PaginatedResponse, SystemMetrics, SystemStats, WorkflowExecution, IntegrationStatus, AdminAction } from '../types';

const API_BASE_URL = getEnvVar('VITE_FASTAPI_BASE_URL');

class FastAPIService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/api/v1`,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Setup interceptors
    this.setupInterceptors();
  }

  // Add auth interceptor
  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_auth_token') || 
                     document.cookie
                       .split('; ')
                       .find(row => row.startsWith('auth_token='))
                       ?.split('=')[1];
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Clear auth token and redirect to login
          localStorage.removeItem('admin_auth_token');
          document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Generic API methods
  private async get<T = any>(url: string, params?: any): Promise<ApiResponse<T>> {
    const response = await this.api.get<ApiResponse<T>>(url, { params });
    return response.data;
  }

  private async post<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.post<ApiResponse<T>>(url, data);
    return response.data;
  }

  private async put<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.put<ApiResponse<T>>(url, data);
    return response.data;
  }

  private async patch<T = any>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.api.patch<ApiResponse<T>>(url, data);
    return response.data;
  }

  private async delete<T = any>(url: string): Promise<ApiResponse<T>> {
    const response = await this.api.delete<ApiResponse<T>>(url);
    return response.data;
  }

  // Generic request method for flexible API calls
  async request<T = any>(
    url: string,
    options?: {
      method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      body?: any;
      params?: any;
      timeout?: number;
    }
  ): Promise<ApiResponse<T>> {
    const method = (options?.method || 'GET').toUpperCase();
    const { body, params, timeout } = options || {};
    const config = timeout ? { timeout } : {};

    try {
      switch (method) {
        case 'GET':
          return await this.api.get<ApiResponse<T>>(url, { params, ...config }).then(r => r.data);
        case 'POST':
          return await this.api.post<ApiResponse<T>>(url, body, config).then(r => r.data);
        case 'PUT':
          return await this.api.put<ApiResponse<T>>(url, body, config).then(r => r.data);
        case 'PATCH':
          return await this.api.patch<ApiResponse<T>>(url, body, config).then(r => r.data);
        case 'DELETE':
          return await this.api.delete<ApiResponse<T>>(url, config).then(r => r.data);
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }
    } catch (error: any) {
      // Handle axios errors
      if (error.response) {
        throw {
          success: false,
          error: error.response.data?.detail || error.response.data?.error || error.message,
          data: null
        };
      }
      throw {
        success: false,
        error: error.message || 'Request failed',
        data: null
      };
    }
  }

  // Paginated requests
  private async getPaginated<T = any>(
    url: string, 
    params?: any
  ): Promise<PaginatedResponse<T>> {
    const response = await this.api.get<PaginatedResponse<T>>(url, { params });
    return response.data;
  }

  // Dashboard data
  async getDashboardData() {
    return this.get<any>('/admin/dashboard');
  }

  // System metrics
  async getSystemMetrics(): Promise<SystemMetrics> {
    try {
      const response = await this.get<SystemMetrics>('/admin/system-metrics');
      return response.data || this.getDefaultMetrics();
    } catch (error) {
      console.error('Error fetching system metrics:', error);
      return this.getDefaultMetrics();
    }
  }

  // System stats
  async getSystemStats(): Promise<SystemStats> {
    try {
      const response = await this.get<SystemStats>('/admin/system-stats');
      return response.data || this.getDefaultStats();
    } catch (error) {
      console.error('Error fetching system stats:', error);
      return this.getDefaultStats();
    }
  }

  // System health
  async getSystemHealth(): Promise<any> {
    try {
      const response = await this.get('/admin/health');
      return response.data || {};
    } catch (error) {
      console.error('Error fetching system health:', error);
      return {};
    }
  }

  // Queue statistics
  async getQueueStats(): Promise<any> {
    try {
      const response = await this.get('/admin/queues');
      return response.data || {};
    } catch (error) {
      console.error('Error fetching queue stats:', error);
      return {};
    }
  }

  // FastAPI logs (for admin monitoring)
  async getFastApiLogs(lines = 100, errorsOnly = false): Promise<{ logs: string[]; file: string; showing: number }> {
    try {
      const response = await this.get<{ logs: string[]; file: string; showing: number }>('/admin/monitoring/logs', {
        lines,
        errors_only: errorsOnly,
      });
      return response.data || { logs: [], file: '', showing: 0 };
    } catch (error) {
      console.error('Error fetching FastAPI logs:', error);
      return { logs: [], file: '', showing: 0 };
    }
  }

  // Workflow executions
  async getWorkflowExecutions(params?: {
    page?: number;
    limit?: number;
    status?: string;
    workspaceId?: string;
  }): Promise<{ data: WorkflowExecution[]; pagination: any }> {
    try {
      const response = await this.getPaginated<WorkflowExecution>('/admin/workflow-executions', params);
      return {
        data: response.data || [],
        pagination: response.pagination || { page: 1, limit: 20, total: 0 }
      };
    } catch (error) {
      console.error('Error fetching workflow executions:', error);
      return { data: [], pagination: { page: 1, limit: 20, total: 0 } };
    }
  }

  // Integration status
  async getIntegrationStatus(): Promise<IntegrationStatus> {
    try {
      const response = await this.get<IntegrationStatus>('/admin/integrations/status');
      return response.data || this.getDefaultIntegrationStatus();
    } catch (error) {
      console.error('Error fetching integration status:', error);
      return this.getDefaultIntegrationStatus();
    }
  }

  // Users list
  async getUsers(params?: { page?: number; limit?: number; search?: string; status?: string; role?: string }) {
    return this.getPaginated<any>('/admin/users', params);
  }

  // User detail
  async getUserDetail(userId: string) {
    return this.get<any>(`/admin/users/${userId}`);
  }

  // Delete user
  async deleteUser(userId: string) {
    return this.delete<any>(`/admin/users/${userId}`);
  }

  // User workspaces
  async getUserWorkspaces(userId: string, params?: { page?: number; limit?: number }) {
    return this.getPaginated<any>(`/admin/users/${userId}/workspaces`, params);
  }

  // User invoices
  async getUserInvoices(userId: string, params?: { page?: number; limit?: number; status?: string }) {
    return this.getPaginated<any>(`/admin/users/${userId}/invoices`, params);
  }

  // User subscriptions
  async getUserSubscriptions(userId: string) {
    return this.get<any>(`/admin/users/${userId}/subscriptions`);
  }

  // Admin: Enroll user in plan
  async enrollUserInPlan(userId: string, workspaceId: string, planName: string) {
    return this.post<any>(`/admin/users/${userId}/subscriptions`, { workspace_id: workspaceId, plan_name: planName });
  }

  // Admin: Change subscription plan (single subscription - legacy)
  async changeUserSubscriptionPlan(userId: string, subscriptionId: string, planName: string) {
    return this.patch<any>(`/admin/users/${userId}/subscriptions/${subscriptionId}/plan`, { plan_name: planName });
  }

  // Admin: Change user's plan as a whole (updates all workspace subscriptions for this user)
  async changeUserPlan(userId: string, planName: string) {
    return this.patch<any>(`/admin/users/${userId}/subscription-plan`, { plan_name: planName });
  }

  // Workspaces list
  async getWorkspaces(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    return this.getPaginated<any>('/admin/workspaces', params);
  }

  // Workflow metrics (per-workspace)
  async getWorkflowMetrics(workspaceId: string) {
    const response = await this.get<any>('/admin/workflow-metrics', { workspace_id: workspaceId });
    return response?.data ?? response;
  }

  // Dead-letter queue list
  async getWorkflowDeadLetter(workspaceId?: string) {
    const params = workspaceId ? { workspace_id: workspaceId } : {};
    const response = await this.get<any[]>('/admin/workflows/dead-letter', params);
    return Array.isArray(response) ? response : (response?.data ?? response ?? []);
  }

  // Redrive DLQ execution
  async redriveWorkflowExecution(executionId: string) {
    return this.post<any>(`/admin/workflows/redrive/${executionId}`);
  }

  // Workspace email provider (Resend or Mailgun)
  async getWorkspaceEmailProvider(workspaceId: string) {
    return this.get<any>(`/admin/workspaces/${workspaceId}/email-provider`);
  }

  async updateWorkspaceEmailProvider(workspaceId: string, provider: 'resend' | 'mailgun') {
    return this.put<any>(`/admin/workspaces/${workspaceId}/email-provider?provider=${provider}`);
  }

  // Workspace credits (super admin)
  async getWorkspaceCredits(workspaceId: string) {
    return this.get<any>(`/admin/workspaces/${workspaceId}/credits`);
  }

  async addWorkspaceCredits(workspaceId: string, amount: number, description?: string) {
    return this.post<any>(`/admin/workspaces/${workspaceId}/credits/add`, { amount, description });
  }

  // Invoices list
  async getInvoices(params?: { page?: number; limit?: number; search?: string; status?: string; workspace_id?: string; user_id?: string }) {
    return this.getPaginated<any>('/admin/invoices', params);
  }

  // Workflows list
  async getWorkflows(params?: { page?: number; limit?: number; search?: string; status?: string }) {
    return this.getPaginated<any>('/admin/workflows', params);
  }

  // Workflow runs
  async getWorkflowRuns(params?: { page?: number; limit?: number; status?: string }) {
    return this.getPaginated<any>('/admin/workflow-runs', params);
  }

  // Recent admin actions
  async getRecentActions(params?: {
    page?: number;
    limit?: number;
  }): Promise<{ data: AdminAction[]; pagination: any }> {
    try {
      const response = await this.getPaginated<AdminAction>('/admin/actions', params);
      return {
        data: response.data || [],
        pagination: response.pagination || { page: 1, limit: 20, total: 0 }
      };
    } catch (error) {
      console.error('Error fetching recent actions:', error);
      return { data: [], pagination: { page: 1, limit: 20, total: 0 } };
    }
  }

  // Default metrics for fallback
  private getDefaultMetrics(): SystemMetrics {
    return {
      users: {
        total: 0,
        active: 0,
        newThisMonth: 0
      },
      workspaces: {
        total: 0,
        active: 0,
        trial: 0
      },
      workflows: {
        total: 0,
        active: 0,
        executionsToday: 0
      },
      system: {
        cpuUsage: 0,
        memoryUsage: 0,
        diskUsage: 0,
        queueStatus: 'healthy'
      }
    };
  }

  // Default stats for fallback
  private getDefaultStats(): SystemStats {
    return {
      users: 0,
      workspaces: 0,
      workflows: 0,
      activeRuns: 0,
      redis: {
        connected_clients: 0,
        used_memory_human: '0B',
        uptime_in_seconds: 0,
      },
      queues: {},
    };
  }

  // Default integration status for fallback
  private getDefaultIntegrationStatus(): IntegrationStatus {
    return {
      email: {
        gmail: { status: 'disabled', lastChecked: new Date().toISOString() },
        mailgun: { status: 'disabled', lastChecked: new Date().toISOString() },
        resend: { status: 'disabled', lastChecked: new Date().toISOString() }
      },
      whatsapp: {
        evolutionApi: { status: 'disabled', lastChecked: new Date().toISOString() },
        retell: { status: 'disabled', lastChecked: new Date().toISOString() }
      },
      payment: {
        razorpay: { status: 'disabled', lastChecked: new Date().toISOString() }
      }
    };
  }
}

export const fastapiService = new FastAPIService();
export default fastapiService;


